import React, { useState } from 'react';

interface ProgressBarWithHandleProps {
  progress: number;
  onProgressChange?: (percentage: number) => void;
  editable?: boolean;
  loading?: boolean;
}

export const ProgressBarWithHandle: React.FC<ProgressBarWithHandleProps> = ({
  progress,
  onProgressChange,
  editable = false,
  loading = false
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [currentProgress, setCurrentProgress] = useState(progress);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!editable) return;
    setIsDragging(true);
    updateProgress(e);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging || !editable) return;
    updateProgress(e);
  };

  const handleMouseUp = () => {
    if (!editable) return;
    setIsDragging(false);
    if (onProgressChange && currentProgress !== progress) {
      onProgressChange(currentProgress);
    }
  };

  const updateProgress = (e: MouseEvent | React.MouseEvent) => {
    const progressBar = e.currentTarget as HTMLElement;
    const rect = progressBar.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setCurrentProgress(Math.round(percentage));
  };

  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging]);

  React.useEffect(() => {
    setCurrentProgress(progress);
  }, [progress]);

  if (loading) {
    return (
      <div className="relative w-full h-3 bg-transparent border-2 border-gray-600 rounded-full animate-pulse">
        <div
          className="absolute h-full bg-white/70 rounded-full"
          style={{ width: '50%' }} // Static width for loading state
        />
      </div>
    );
  }

  return (
    <div 
        onMouseDown={handleMouseDown} 
        className={`relative w-full h-3 bg-transparent border-2 border-cyan-500 rounded-full shadow-2xl shadow-cyan-500 outline-8 outline-cyan-500 progress-bar-handle-container ${editable && 'cursor-pointer'}`}
    >
      <div
        className="absolute h-full bg-cyan-500 rounded-full transition-all duration-300"
        style={{ width: `${currentProgress}%` }}
      />
      <div
        className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 bg-cyan-700 rounded-full shadow-md ${editable && 'cursor-grab active:cursor-grabbing'}`}
        style={{ left: `calc(${currentProgress}% - 0.75rem)` }}
        onMouseDown={handleMouseDown}
      />
    </div>
  );
};